import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useTheme } from '@/stores/theme';
import { MAKHE_LOCKUP_ALT, MAKHE_SLOGAN } from '@/lib/brand';

type BrandMarkProps = {
  to?: string | null;
  /** Full purchased lockup (M + Makhe + neighbour + slogan). */
  lockup?: boolean;
  /** Typeset slogan next to the compact mark. Hidden on small screens. */
  withSlogan?: boolean;
  /** Logo colour variant. `auto` picks based on the active theme. */
  variant?: 'light' | 'dark' | 'auto';
  className?: string;
  /** Compact mark height, or max height for the lockup. */
  size?: number;
};

/**
 * Compact house-M, or the full lockup image.
 * `light` mark (glow) sits on dark UI; `dark` mark (purple house-M) on pale surfaces.
 */
export function BrandMark({
  to = '/',
  lockup = false,
  withSlogan = false,
  variant = 'auto',
  className,
  size = 28,
}: BrandMarkProps) {
  const theme = useTheme((s) => s.theme);
  const resolvedVariant =
    variant === 'auto' ? (theme === 'dark' ? 'light' : 'dark') : variant;

  const content = lockup ? (
    <img
      src="/logo-lockup.png"
      alt={MAKHE_LOCKUP_ALT}
      className="h-auto max-w-full object-contain object-left dark:rounded-2xl dark:bg-white dark:p-3"
      style={{ maxHeight: size }}
      decoding="async"
    />
  ) : (
    <>
      <img
        src={resolvedVariant === 'dark' ? '/logo-mark.png' : '/logo-mark-light.png'}
        alt=""
        height={size}
        className="shrink-0 object-contain"
        style={{ height: size, width: 'auto' }}
        decoding="async"
      />
      {withSlogan && (
        <span className="hidden text-[11px] uppercase tracking-widest text-makhe-muted md:inline">
          {MAKHE_SLOGAN}
        </span>
      )}
    </>
  );

  const classes = cn(
    'inline-flex items-center gap-3 text-makhe-ink',
    lockup && 'max-w-full',
    className,
  );

  if (to == null) {
    return (
      <span className={classes} aria-label={lockup ? undefined : 'Makhe'}>
        {content}
      </span>
    );
  }

  return (
    <Link to={to} className={classes} aria-label="Makhe home">
      {content}
    </Link>
  );
}
